import { getLandOrPortExThumb } from 'common/utils/exerciseUtils'

// The optional tap-through button on a coach post. Field names intentionally match
// the old announcements records (linkType/link/openInDefaultBrowser/scene/videoId/
// programId/workoutId) so the mobile rendering port is one-to-one. exerciseId came
// later (2026-10) and follows the same pattern, as do collectionId and programGroupId.
// A series is a "program group" in the records and in the app, so its button type is
// `programGroup`; only the label says "Series".
//
// How a post's photo is displayed depends on whether the post has a button:
//   - no button: the photo renders large, under the post text.
//   - any button: the photo becomes the button card's thumbnail instead, and no
//     large photo is shown. The photo wins over the linked content's own artwork.
// The dashboard form and the mobile app both follow this rule, so what the coach
// sees while writing is what members see.

export const GROUP_CALL_TYPE = 'groupCall'

export const BUTTON_TYPES = [
  { value: 'none', displayValue: 'None' },
  { value: 'link', displayValue: 'Link' },
  { value: GROUP_CALL_TYPE, displayValue: 'Group call' },
  { value: 'scene', displayValue: 'App page' },
  { value: 'video', displayValue: 'Video' },
  { value: 'program', displayValue: 'Program' },
  { value: 'workout', displayValue: 'Workout' },
  { value: 'exercise', displayValue: 'Exercise' },
  { value: 'collection', displayValue: 'Collection' },
  { value: 'programGroup', displayValue: 'Series' },
]

export const REPLY_BUTTON_TYPES = BUTTON_TYPES.filter(({ value }) => value !== 'none' && value !== GROUP_CALL_TYPE)

export const getButtonTypeLabel = (linkType) => BUTTON_TYPES.find(({ value }) => value === linkType)?.displayValue || ''

// Posts without a button have no linkType at all (the field is left out on create
// and cleared to null on edit).
export const hasPostButton = (post) => Boolean(post?.linkType)

export const hasReplyAttachment = (reply) => reply?.authorIsCoach === true && hasPostButton(reply)

export const buildReplyAttachmentFields = ({
  attachment,
  videos,
  programs,
  workouts,
  exercises,
  collections,
  programGroups,
  exVidOrientation,
  scenes,
}) => {
  if (!attachment?.buttonType) return undefined
  const imageUrl = getBorrowedThumbnail({
    ...attachment,
    videos,
    programs,
    workouts,
    exercises,
    collections,
    programGroups,
    exVidOrientation,
    scenes,
  })
  return stripNullFields({ ...buildButtonFields({ ...attachment, link: (attachment.link || '').trim() }), imageUrl })
}

// The attachment a reply was saved with, in the two shapes the reply box keeps: the
// attachment form's values (`draft`), so changing it opens on what is attached now,
// and the saved fields, which draw the preview card and are written back unchanged
// when only the text is edited. Null when the reply has no attachment.
export const getReplyAttachment = (reply) => {
  if (!hasReplyAttachment(reply)) return null
  const fields = stripNullFields(
    Object.fromEntries(
      [...Object.keys(buildButtonFields({})), 'imageUrl'].map((key) => [
        key,
        reply[key] === undefined ? null : reply[key],
      ])
    )
  )
  return {
    fields,
    draft: {
      buttonType: reply.linkType,
      link: reply.link || '',
      openInDefaultBrowser: reply.openInDefaultBrowser === true,
      scene: reply.scene || '',
      videoId: reply.videoId || '',
      programId: reply.programId || '',
      workoutId: reply.workoutId || '',
      exerciseId: reply.exerciseId || '',
      collectionId: reply.collectionId || '',
      programGroupId: reply.programGroupId || '',
    },
  }
}

// What an edited reply writes: its text and every attachment field. The ones the new
// attachment does not use are null, which deletes them, so a changed or removed
// attachment leaves nothing of the old one behind — a video id under a program
// attachment, or the old content's borrowed thumbnail.
export const buildReplyEditFields = ({ text, attachmentFields }) => ({
  text,
  ...buildButtonFields({}),
  imageUrl: null,
  ...attachmentFields,
})

export const isGroupCallPost = (post) => post?.linkType === GROUP_CALL_TYPE

// The exercise list the picker offers. The exercise card on the phone opens the
// exercise's demo video, so an exercise with no video for the org's orientation
// would open an empty screen and is left out. The library hook reports which ones
// have a video (`exIdHasVideo`), and `isFiller` is its not-yet-loaded marker.
export const getAttachableExercises = (exercisesData) => {
  if (!exercisesData || exercisesData.isFiller || !exercisesData.exercises) return {}
  return Object.fromEntries(
    Object.entries(exercisesData.exercises).filter(([exId, ex]) => ex && exercisesData.exIdHasVideo?.[exId])
  )
}

// The collections or series the picker offers. One with no name is left out, as the
// App Layout picker leaves it out: the card would have no title on either surface.
export const getAttachableNamedItems = (itemsData) => {
  if (!itemsData || itemsData.isLoading) return {}
  return Object.fromEntries(
    Object.entries(itemsData).filter(([, item]) => item && typeof item === 'object' && item.name?.trim())
  )
}

export const EXERCISE_UNAVAILABLE_MESSAGE = 'This exercise was deleted or has no demo video. Choose another.'

// A record saved earlier can point at an exercise that has since lost its video or been
// deleted. The picker leaves it out and shows as empty, so saving it would attach
// something the coach cannot see. Only judged once the exercise list has loaded.
export const isUnattachableExercise = (exercisesData, exerciseId) =>
  Boolean(exerciseId) &&
  Boolean(exercisesData) &&
  !exercisesData.isFiller &&
  !getAttachableExercises(exercisesData)[exerciseId]

export const getGroupCallState = (post, currentTime = Date.now()) => {
  if (!isGroupCallPost(post)) return null

  const start = Number(post?.callStartAt)
  const end = Number(post?.callEndAt)
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null

  if (currentTime < start) return 'upcoming'
  if (currentTime < end) return 'live'
  return 'ended'
}

// "https://www.youtube.com/watch?v=x" -> "youtube.com". Mirrors mobile's
// PostButton helper so link cards title the same on both surfaces.
export const getLinkDomain = (link) => {
  if (typeof link !== 'string') return ''
  const match = link.match(/^https?:\/\/(?:www\.)?([^/:?#]+)/i)
  return match ? match[1] : ''
}

// The post-record fields for the chosen button. Every button field is always
// present (null when unused) so an edit that changes the button type also clears
// the previous type's fields in the same write.
export const buildButtonFields = ({
  buttonType,
  link,
  openInDefaultBrowser,
  scene,
  videoId,
  programId,
  workoutId,
  exerciseId,
  collectionId,
  programGroupId,
  callStartAt,
  callEndAt,
  callTimeZone,
}) => {
  const empty = {
    linkType: null,
    link: null,
    openInDefaultBrowser: null,
    scene: null,
    videoId: null,
    programId: null,
    workoutId: null,
    exerciseId: null,
    collectionId: null,
    programGroupId: null,
    callStartAt: null,
    callEndAt: null,
    callTimeZone: null,
  }

  if (buttonType === 'link') {
    return { ...empty, linkType: 'link', link, openInDefaultBrowser: openInDefaultBrowser === true }
  }
  if (buttonType === GROUP_CALL_TYPE) {
    // Call links are Zoom/Meet/Teams URLs that deep-link into their own app, so
    // they always leave the in-app browser — the coach gets no choice here.
    return {
      ...empty,
      linkType: GROUP_CALL_TYPE,
      link,
      openInDefaultBrowser: true,
      callStartAt: Number(callStartAt),
      callEndAt: Number(callEndAt),
      callTimeZone,
    }
  }
  if (buttonType === 'scene') {
    return { ...empty, linkType: 'scene', scene }
  }
  if (buttonType === 'video') {
    return { ...empty, linkType: 'video', videoId }
  }
  if (buttonType === 'program') {
    return { ...empty, linkType: 'program', programId }
  }
  if (buttonType === 'workout') {
    return { ...empty, linkType: 'workout', programId, workoutId }
  }
  if (buttonType === 'exercise') {
    return { ...empty, linkType: 'exercise', exerciseId }
  }
  if (buttonType === 'collection') {
    return { ...empty, linkType: 'collection', collectionId }
  }
  if (buttonType === 'programGroup') {
    return { ...empty, linkType: 'programGroup', programGroupId }
  }
  return empty
}

// Strip null values for post creation (a new post simply omits unused fields —
// nulls are only needed on edit, where they delete previous values).
export const stripNullFields = (fields) =>
  Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== null && value !== undefined))

// A post with a content button and no photo of its own borrows the linked
// content's thumbnail — snapshotted at save time so phones render it with zero
// extra lookups (same reasoning as the denormalized author fields). A photo the
// coach uploaded always wins over this.
export const getBorrowedThumbnail = ({
  buttonType,
  scene,
  videoId,
  programId,
  workoutId,
  exerciseId,
  collectionId,
  programGroupId,
  videos,
  programs,
  workouts,
  exercises,
  collections,
  programGroups,
  exVidOrientation,
  scenes,
}) => {
  if (buttonType === 'video') return videos?.[videoId]?.previewImg || null
  if (buttonType === 'program') return programs?.[programId]?.previewImg || null
  if (buttonType === 'workout') return workouts?.[workoutId]?.previewImg || null
  if (buttonType === 'collection') return collections?.[collectionId]?.previewImg || null
  if (buttonType === 'programGroup') return programGroups?.[programGroupId]?.previewImg || null
  // An exercise has a thumbnail per video orientation; the org's setting picks it.
  if (buttonType === 'exercise') return getLandOrPortExThumb(exVidOrientation, exercises?.[exerciseId]) || null
  if (buttonType === 'scene') return scenes?.find((s) => s.value === scene)?.defaultImg || null
  return null
}
